import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import { id } from 'react-day-picker/locale';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { controlClassName } from 'components/list/styles';

const toDate = (d) => {
  if (!d) return undefined;
  if (d instanceof Date) return d;
  return dayjs(d).toDate();
};

const SHORTCUTS = [
  { key: 'today', label: 'Hari Ini' },
  { key: 'yesterday', label: 'Kemarin' },
  { key: 'thisWeek', label: 'Minggu Ini' },
  { key: 'lastWeek', label: 'Minggu Lalu' },
  { key: 'thisMonth', label: 'Bulan Ini' },
  { key: 'lastMonth', label: 'Bulan Lalu' },
];

const getShortcutRange = (key) => {
  const now = dayjs();
  switch (key) {
    case 'today':
      return { startDate: now.toDate(), endDate: now.toDate() };
    case 'yesterday':
      return {
        startDate: now.subtract(1, 'day').toDate(),
        endDate: now.subtract(1, 'day').toDate(),
      };
    case 'thisWeek':
      return { startDate: now.startOf('week').toDate(), endDate: now.endOf('week').toDate() };
    case 'lastWeek':
      return {
        startDate: now.subtract(1, 'week').startOf('week').toDate(),
        endDate: now.subtract(1, 'week').endOf('week').toDate(),
      };
    case 'thisMonth':
      return { startDate: now.startOf('month').toDate(), endDate: now.endOf('month').toDate() };
    case 'lastMonth':
      return {
        startDate: now.subtract(1, 'month').startOf('month').toDate(),
        endDate: now.subtract(1, 'month').endOf('month').toDate(),
      };
    default:
      return { startDate: null, endDate: null };
  }
};

const Datepicker = ({
  value = { startDate: null, endDate: null },
  onChange,
  placeholder = 'Pilih Tanggal',
  showShortcuts = false,
  maxDate,
  readOnly = false,
  disabled = false,
  id: inputId,
  asSingle = false,
  useRange = true,
  containerClassName = '',
  inputClassName = '',
  toggleClassName = '',
  _popoverDirection = 'down',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popupStyle, setPopupStyle] = useState({ display: 'none' });
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const isDisabled = disabled || readOnly;
  const isRange = asSingle ? false : useRange;
  const [internalValue, setInternalValue] = useState(value);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setInternalValue(value);
    }
  }, [isOpen, value]);

  const updatePosition = useCallback(() => {
    if (!containerRef.current || !popupRef.current) return;

    const triggerRect = containerRef.current.getBoundingClientRect();
    const popupRect = popupRef.current.getBoundingClientRect();

    let left = triggerRect.left;

    const maxLeft = window.innerWidth - popupRect.width - 8;
    left = Math.min(Math.max(8, left), maxLeft);

    const spaceBelow = window.innerHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    let top;

    if (spaceBelow >= popupRect.height + 8) {
      top = triggerRect.bottom + 8;
    } else if (spaceAbove >= popupRect.height + 8) {
      top = triggerRect.top - popupRect.height - 8;
    } else {
      if (spaceBelow > spaceAbove) {
        top = triggerRect.bottom + 8;
      } else {
        top = Math.max(8, triggerRect.top - popupRect.height - 8);
      }
    }

    setPopupStyle({
      position: 'fixed',
      left,
      top,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!isOpen || !popupRef.current) return;

    const el = popupRef.current;

    const ro = new ResizeObserver(() => {
      updatePosition();
    });

    ro.observe(el);
    resizeObserverRef.current = ro;

    requestAnimationFrame(updatePosition);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (popupRef.current?.contains(e.target)) return;
      if (containerRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isOpen]);

  const formatDisplay = () => {
    const s = value?.startDate;
    const e = value?.endDate;
    if (!s && !e) return '';
    if (isRange && s && e) {
      return `${dayjs(s).format('DD/MM/YYYY')} - ${dayjs(e).format('DD/MM/YYYY')}`;
    }
    return dayjs(s || e).format('DD/MM/YYYY');
  };

  const handleRangeSelect = (range) => {
    if (!range) {
      setInternalValue({ startDate: null, endDate: null });
      return;
    }
    setInternalValue({
      startDate: range.from || null,
      endDate: range.to || null,
    });
  };

  const handleSingleSelect = (date) => {
    if (maxDate && date) {
      const max = toDate(maxDate);
      if (max && dayjs(date).isAfter(dayjs(max), 'day')) return;
    }
    onChange({
      startDate: date || null,
      endDate: date || null,
    });
    if (date) setIsOpen(false);
  };

  const handleShortcut = (key) => {
    const range = getShortcutRange(key);
    if (maxDate) {
      const max = toDate(maxDate);
      if (max && range.endDate && dayjs(range.endDate).isAfter(dayjs(max), 'day')) {
        range.endDate = max;
      }
    }
    onChange(range);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange({ startDate: null, endDate: null });
  };

  const maxDateObj = toDate(maxDate);
  const disabledDays = maxDateObj ? { after: maxDateObj } : undefined;

  const hasValue = value?.startDate || value?.endDate;

  return (
    <div ref={containerRef} className={`relative inline-block ${containerClassName}`}>
      {/* Input Field - Styled like form input */}
      <div
        id={inputId}
        className={`${controlClassName} flex items-center gap-2 ${isDisabled ? 'cursor-not-allowed bg-gray-100! text-gray-400! opacity-70' : 'cursor-pointer'} ${inputClassName}`}
        role='button'
        aria-expanded={isOpen && !isDisabled && !isDisabled}
        aria-disabled={isDisabled}
        aria-haspopup='dialog'
        tabIndex={isDisabled ? -1 : 0}
        onClick={() => {
          if (!isDisabled) setIsOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
      >
        <CalendarIcon
          className={`w-4 h-4 flex-shrink-0 ${!isDisabled && hasValue ? 'text-teal-500' : toggleClassName || 'text-gray-400'}`}
        />
        <span
          className={`text-sm min-w-0 flex-1 truncate ${!isDisabled && hasValue ? 'text-gray-700' : 'text-gray-400'}`}
        >
          {formatDisplay() || placeholder}
        </span>
        {hasValue && !isDisabled && (
          <button
            type='button'
            aria-label='Hapus tanggal'
            onClick={handleClear}
            className='flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors p-1 rounded-full hover:bg-gray-100'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='12'
              height='12'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18'></line>
              <line x1='6' y1='6' x2='18' y2='18'></line>
            </svg>
          </button>
        )}
      </div>

      {/* Popup Calendar */}
      {isOpen &&
        createPortal(
          <div
            ref={popupRef}
            style={popupStyle}
            role='dialog'
            aria-label='Pilih tanggal'
            onKeyDown={(event) => {
              if (event.key === 'Escape') setIsOpen(false);
            }}
            className='flex max-h-[calc(100dvh-1rem)] max-w-[calc(100vw-1rem)] overflow-auto rounded-2xl border border-gray-200 bg-white shadow-xl'
          >
            {showShortcuts && isRange && (
              <div className='flex flex-col gap-1 py-3 px-2 border-r border-gray-100 bg-gray-50/50 min-w-[108px]'>
                <div className='px-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide'>
                  Shortcuts
                </div>
                {SHORTCUTS.map((s) => (
                  <button
                    key={s.key}
                    type='button'
                    className='px-3 py-2 text-xs font-medium text-left rounded-lg text-gray-600 hover:bg-teal-500 hover:text-white transition-all whitespace-nowrap'
                    onClick={() => handleShortcut(s.key)}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            )}
            <div className='flex flex-col'>
              <div className='px-3 pt-1.5 pb-1 '>
                <DayPicker
                  classNames={{
                    root: 'relative',
                    months: 'flex flex-col gap-4 sm:flex-row',
                    month: 'min-w-56',
                    month_caption:
                      'flex h-10 items-center px-2 text-sm font-semibold text-gray-900',
                    caption_label: 'flex-1',
                    nav: 'absolute right-0 top-0 flex h-10 items-center gap-1',
                    button_previous:
                      'flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30',
                    button_next:
                      'flex h-8 w-8 items-center justify-center rounded-lg hover:bg-gray-100 disabled:opacity-30',
                    month_grid: 'w-full border-collapse',
                    weekday: 'h-8 text-center text-xs font-medium text-gray-400',
                    day: 'h-8 w-8 p-0 text-center text-xs',
                    day_button:
                      'h-8 w-8 rounded-full font-medium transition-colors hover:bg-teal-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 disabled:cursor-not-allowed',
                    today: '[&_button]:font-bold [&_button]:ring-1 [&_button]:ring-teal-500',
                    selected:
                      '[&_button]:bg-teal-500 [&_button]:text-white [&_button:hover]:bg-teal-600',
                    range_start:
                      'rounded-l-full bg-teal-50 [&_button]:bg-teal-500 [&_button]:text-white',
                    range_end:
                      'rounded-r-full bg-teal-50 [&_button]:bg-teal-500 [&_button]:text-white',
                    range_middle:
                      'bg-teal-50 [&_button]:rounded-none! [&_button]:bg-teal-50! [&_button]:text-teal-700!',
                    outside: 'opacity-30',
                    disabled: 'opacity-30',
                    hidden: 'invisible',
                  }}
                  mode={isRange ? 'range' : 'single'}
                  numberOfMonths={isRange ? 2 : 1}
                  pagedNavigation={isRange}
                  required={!isRange}
                  selected={
                    isRange
                      ? {
                          from: toDate(internalValue?.startDate),
                          to: toDate(internalValue?.endDate),
                        }
                      : toDate(value?.startDate) || toDate(value?.endDate)
                  }
                  onSelect={isRange ? handleRangeSelect : handleSingleSelect}
                  disabled={disabledDays}
                  defaultMonth={toDate(value?.startDate) || new Date()}
                  locale={id}
                  weekStartsOn={1}
                  components={{
                    Chevron: ({ orientation }) =>
                      orientation === 'left' ? (
                        <ChevronLeftIcon strokeWidth={2.5} className='w-4 h-4 text-gray-500' />
                      ) : (
                        <ChevronRightIcon strokeWidth={2.5} className='w-4 h-4 text-gray-500' />
                      ),
                  }}
                />
              </div>
              <div className='flex justify-end gap-2 px-3 py-2 border-t border-gray-100 bg-gray-50/30'>
                <button
                  type='button'
                  className='px-3 py-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors'
                  onClick={() => setIsOpen(false)}
                >
                  Batal
                </button>
                {isRange && (
                  <button
                    type='button'
                    disabled={!internalValue?.startDate || !internalValue?.endDate}
                    className='disabled:cursor-not-allowed disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white bg-teal-500 rounded-lg hover:bg-teal-600 transition-colors shadow-sm shadow-teal-500/30'
                    onClick={() => {
                      onChange(internalValue);
                      setIsOpen(false);
                    }}
                  >
                    OK
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Datepicker;
